import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { toast } from "sonner";
import { Plus, Star, MoreHorizontal, Pencil, Trash2, Share2, Search, PenTool } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Avatar } from "../components/ui-bits";
import { formatDistanceToNow } from "date-fns";
import { cn } from "../lib/utils";

const FILTERS = [
  { key: "all", label: "All Whiteboards" },
  { key: "mine", label: "My Whiteboards" },
  { key: "fav", label: "Favorites" },
  { key: "recent", label: "Recents" },
];

export default function Whiteboards() {
  const { user } = useAuth();
  const { users } = useData();
  const [boards, setBoards] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [renameBoard, setRenameBoard] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [shareBoard, setShareBoard] = useState(null);
  const [shareIds, setShareIds] = useState([]);
  const navigate = useNavigate();

  // api is a module-level import (non-reactive); only stable state setters are referenced.
  const load = useCallback(async () => {
    const { data } = await api.get("/whiteboards");
    setBoards(data);
  }, [setBoards]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const shareableUsers = useMemo(() => users.filter((u) => u.id !== shareBoard?.owner_id), [users, shareBoard]);

  const visible = useMemo(() => {
    let out = boards;
    if (filter === "mine") out = out.filter((b) => b.owner_id === user?.id);
    if (filter === "fav") out = out.filter((b) => (b.favorite_by || []).includes(user?.id));
    if (filter === "recent") out = out.slice(0, 8);
    if (search.trim()) out = out.filter((b) => b.name.toLowerCase().includes(search.toLowerCase()));
    return out;
  }, [boards, filter, search, user]);

  const create = async () => {
    try {
      const { data } = await api.post("/whiteboards", { name: "Untitled whiteboard" });
      navigate(`/whiteboards/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleFav = async (b) => {
    try { await api.post(`/whiteboards/${b.id}/favorite`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete whiteboard "${b.name}"?`)) return;
    try { await api.delete(`/whiteboards/${b.id}`); load(); toast.success("Whiteboard deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveRename = async () => {
    if (!renameValue.trim()) return;
    try {
      await api.patch(`/whiteboards/${renameBoard.id}`, { name: renameValue.trim() });
      setRenameBoard(null);
      load();
      toast.success("Renamed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveShare = async () => {
    try {
      await api.patch(`/whiteboards/${shareBoard.id}`, { shared_with: shareIds });
      setShareBoard(null);
      load();
      toast.success("Sharing updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="h-full flex flex-col md:flex-row" data-testid="whiteboards-page">
      <div className="w-full md:w-56 shrink-0 border-b md:border-b-0 md:border-r border-border bg-surface p-3 flex md:block items-center md:items-stretch gap-1 overflow-x-auto">
        <div className="flex items-center justify-between mb-2">
          <span className="font-heading font-bold text-sm">Whiteboards</span>
          <button data-testid="new-whiteboard-mini" onClick={create} className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-[#E31E24] transition-colors">
            <Plus size={15} />
          </button>
        </div>
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`wb-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={cn("w-auto md:w-full whitespace-nowrap text-left px-2.5 h-8 rounded-md text-sm transition-colors md:mb-0.5 shrink-0",
              filter === f.key ? "bg-[#E31E24]/10 text-[#E31E24] font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-w-0 overflow-y-auto p-4 md:p-6">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <h2 className="font-heading text-xl font-bold tracking-tight">{FILTERS.find((f) => f.key === filter)?.label}</h2>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="wb-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search whiteboards…"
                className="h-8 w-48 pl-7 pr-2 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            </div>
            <button data-testid="new-whiteboard-button" onClick={create}
              className="h-8 px-3 flex items-center gap-1.5 text-sm font-medium rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white transition-colors">
              <Plus size={15} />New Whiteboard
            </button>
          </div>
        </div>

        {visible.length === 0 && (
          <div className="text-center py-20 text-muted-foreground" data-testid="wb-empty">
            <PenTool size={28} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No whiteboards here yet. Create one to sketch out your work.</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visible.map((b) => {
            const fav = (b.favorite_by || []).includes(user?.id);
            const shared = (b.shared_with || []).length > 0;
            return (
              <div key={b.id} data-testid={`wb-card-${b.id}`} className="group bg-card border border-border rounded-lg overflow-hidden hover:border-[#E31E24]/40 transition-colors">
                <button data-testid={`wb-open-${b.id}`} onClick={() => navigate(`/whiteboards/${b.id}`)} className="block w-full">
                  <div className="h-36 bg-white flex items-center justify-center border-b border-border overflow-hidden">
                    {b.content
                      ? <img src={b.content} alt={b.name} className="w-full h-full object-cover" />
                      : <PenTool size={26} className="text-slate-300" />}
                  </div>
                </button>
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                      {b.name}
                      {shared && <Share2 size={11} className="text-muted-foreground shrink-0" />}
                    </p>
                    <p className="text-[11px] text-muted-foreground">Edited {formatDistanceToNow(new Date(b.updated_at), { addSuffix: true })}</p>
                  </div>
                  <Avatar name={b.owner_name} userId={b.owner_id} size="sm" />
                  <button data-testid={`wb-fav-${b.id}`} onClick={() => toggleFav(b)}
                    className={cn("h-6 w-6 flex items-center justify-center rounded transition-colors", fav ? "text-amber-400" : "text-muted-foreground/40 hover:text-amber-400")}>
                    <Star size={14} fill={fav ? "currentColor" : "none"} />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`wb-menu-${b.id}`} className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:text-foreground">
                        <MoreHorizontal size={14} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem data-testid={`wb-rename-${b.id}`} onClick={() => { setRenameBoard(b); setRenameValue(b.name); }}>
                        <Pencil size={14} className="mr-2" />Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`wb-share-${b.id}`} onClick={() => { setShareBoard(b); setShareIds(b.shared_with || []); }}>
                        <Share2 size={14} className="mr-2" />Share
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`wb-delete-${b.id}`} className="text-[#E31E24]" onClick={() => remove(b)}>
                        <Trash2 size={14} className="mr-2" />Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!renameBoard} onOpenChange={(v) => !v && setRenameBoard(null)}>
        <DialogContent className="sm:max-w-xs" data-testid="wb-rename-dialog">
          <DialogHeader><DialogTitle className="font-heading">Rename whiteboard</DialogTitle></DialogHeader>
          <input autoFocus data-testid="wb-rename-input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveRename()}
            className="w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          <div className="flex justify-end gap-2">
            <button data-testid="wb-rename-cancel" onClick={() => setRenameBoard(null)} className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">Cancel</button>
            <button data-testid="wb-rename-submit" onClick={saveRename} className="h-8 px-4 text-sm rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white font-medium transition-colors">Save</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shareBoard} onOpenChange={(v) => !v && setShareBoard(null)}>
        <DialogContent className="sm:max-w-xs" data-testid="wb-share-dialog">
          <DialogHeader><DialogTitle className="font-heading">Share "{shareBoard?.name}"</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Selected members can view and edit this whiteboard.</p>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {shareableUsers.map((u) => (
              <button key={u.id} data-testid={`wb-share-user-${u.id}`}
                onClick={() => setShareIds((p) => (p.includes(u.id) ? p.filter((x) => x !== u.id) : [...p, u.id]))}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted text-sm">
                <input type="checkbox" readOnly checked={shareIds.includes(u.id)} className="accent-[#E31E24]" />
                <Avatar name={u.name} userId={u.id} size="sm" online={u.online} />
                <span className="truncate">{u.name}</span>
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button data-testid="wb-share-cancel" onClick={() => setShareBoard(null)} className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">Cancel</button>
            <button data-testid="wb-share-submit" onClick={saveShare} className="h-8 px-4 text-sm rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white font-medium transition-colors">Share</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
